import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MatTreeModule } from '@angular/material/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { AdminService, AdminTreeNode, AdminNodeCreatePayload, AdminNodeUpdatePayload } from '../../../services/admin.service';

// Updated interface to match the API response
export interface AdminTreeNodeLocal extends AdminTreeNode {
  // Additional local properties if needed
}

@Component({
  selector: 'app-admin-tree',
  standalone: true,
  imports: [
    CommonModule,
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule
  ],
  templateUrl: './admin-tree.component.html',
  styleUrl: './admin-tree.component.css'
})
export class AdminTreeComponent implements OnInit, OnDestroy {
  @Input() isCollapsed: boolean = false;
  @Input() showEditMode: boolean = false;

  @Output() nodeSelected = new EventEmitter<AdminTreeNodeLocal>();
  @Output() editModeToggled = new EventEmitter<boolean>();
  @Output() nodeAdded = new EventEmitter<{parent: AdminTreeNodeLocal, newNode: AdminTreeNodeLocal}>();
  @Output() nodeEdited = new EventEmitter<AdminTreeNodeLocal>();
  @Output() nodeDeleted = new EventEmitter<AdminTreeNodeLocal>();
  @Output() treeRefreshed = new EventEmitter<void>();
  @Output() collapseStateChanged = new EventEmitter<boolean>();

  treeControl = new NestedTreeControl<AdminTreeNodeLocal>(node => node.children);
  dataSource = new MatTreeNestedDataSource<AdminTreeNodeLocal>();

  // State management
  selectedNode: AdminTreeNodeLocal | null = null;
  isEditMode: boolean = false;
  isTreeExpanded: boolean = false;
  isLoading: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadAdminTree();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAdminTree(): void {
    this.isLoading = true;
    this.adminService.getAdminTree()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          console.log('Admin tree loaded:', response);
          let treeData = response.tree || [];
          
          // Inject Image Processing Tool node under Python Scripts
          treeData = this.injectImageProcessingToolNode(treeData);
          
          this.dataSource.data = treeData;
          
          // Auto-expand all nodes on initial load
          if (this.dataSource.data.length > 0) {
            this.treeControl.dataNodes = this.dataSource.data;
            this.treeControl.expandAll();
          }
        },
        error: (error) => {
          console.error('Error loading admin tree:', error);
          this.showError('Failed to load admin tree. Please try again.');
          // Fallback to empty data
          this.dataSource.data = [];
        }
      });
  }

  /**
   * Inject Image Processing Tool node under Python Scripts if not already present
   */
  private injectImageProcessingToolNode(treeData: AdminTreeNodeLocal[]): AdminTreeNodeLocal[] {
    const imageToolNode: AdminTreeNodeLocal = {
      id: 9999,
      node_id: 'image-processing-tool',
      name: 'Image Processing Tool',
      type: 'action',
      expandable: false,
      level: 1,
      parent_id: 'python-scripts',
      icon: 'image',
      description: 'Three-level image cutting and joining tool for question paper processing',
      sort_order: 0,
      is_active: true,
      children: []
    };

    // Find Python Scripts node and add the image tool as first child
    const findAndInject = (nodes: AdminTreeNodeLocal[]): boolean => {
      for (const node of nodes) {
        if (node.node_id === 'python-scripts') {
          // Check if already exists
          const exists = node.children?.some(c => c.node_id === 'image-processing-tool');
          if (!exists) {
            node.children = [imageToolNode, ...(node.children || [])];
            console.log('Injected Image Processing Tool node under Python Scripts');
          }
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findAndInject(node.children)) {
            return true;
          }
        }
      }
      return false;
    };

    findAndInject(treeData);
    return treeData;
  }

  hasChild = (_: number, node: AdminTreeNodeLocal) => !!node.children && node.children.length > 0;

  onNodeSelect(node: AdminTreeNodeLocal): void {
    this.selectedNode = node;
    this.nodeSelected.emit(node);
  }

  toggleAllNodes(): void {
    this.isTreeExpanded = !this.isTreeExpanded;
    if (this.isTreeExpanded) {
      this.treeControl.expandAll();
    } else {
      this.treeControl.collapseAll();
    }
  }

  toggleEditMode(event: any): void {
    this.isEditMode = event.checked;
    this.editModeToggled.emit(this.isEditMode);
  }

  refreshTree(): void {
    console.log('Refreshing admin tree...');
    this.loadAdminTree();
    this.treeRefreshed.emit();
  }

  // Edit functionality methods
  canEditDeleteNode(node: AdminTreeNodeLocal): boolean {
    // Only allow editing/deleting action nodes (level 1+)
    return this.isEditMode && node.level > 0;
  }

  canAddNode(node: AdminTreeNodeLocal): boolean {
    // Allow adding child nodes to operation nodes (level 0)
    return this.isEditMode && node.type === 'operation';
  }

  addNode(parentNode: AdminTreeNodeLocal): void {
    const newNodeName = prompt('Enter new node name:');
    if (!newNodeName || newNodeName.trim() === '') {
      return;
    }

    // Generate a unique node_id based on parent and siblings
    const newNodeId = this.generateNodeId(parentNode, newNodeName);
    
    const payload: AdminNodeCreatePayload = {
      node_id: newNodeId,
      name: newNodeName.trim(),
      type: 'action',
      expandable: true,
      level: parentNode.level + 1,
      parent_id: parentNode.node_id,
      icon: 'settings',
      description: '',
      sort_order: (parentNode.children?.length || 0) + 1
    };

    this.isLoading = true;
    this.adminService.createAdminNode(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          console.log('Node created successfully:', response);
          this.showSuccess('Node added successfully!');
          this.refreshTree(); // Reload the tree to show the new node
          
          // Emit the event
          if (response.node) {
            this.nodeAdded.emit({parent: parentNode, newNode: response.node});
          }
        },
        error: (error) => {
          console.error('Error creating node:', error);
          this.showError('Failed to add node. Please try again.');
        }
      });
  }

  editNode(node: AdminTreeNodeLocal): void {
    const newName = prompt('Enter new node name:', node.name);
    if (!newName || newName.trim() === '' || newName.trim() === node.name) {
      return;
    }

    const payload: AdminNodeUpdatePayload = {
      name: newName.trim()
    };

    this.isLoading = true;
    this.adminService.updateAdminNode(node.node_id, payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          console.log('Node updated successfully:', response);
          this.showSuccess('Node renamed successfully!');
          this.refreshTree(); // Reload the tree to show the updated node
          
          // Emit the event
          if (response.node) {
            this.nodeEdited.emit(response.node);
          }
        },
        error: (error) => {
          console.error('Error updating node:', error);
          this.showError('Failed to rename node. Please try again.');
        }
      });
  }

  deleteNode(node: AdminTreeNodeLocal): void {
    const confirmDelete = confirm(`Are you sure you want to delete "${node.name}"?`);
    if (!confirmDelete) {
      return;
    }

    this.isLoading = true;
    this.adminService.deleteAdminNode(node.node_id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          console.log('Node deleted successfully:', response);
          this.showSuccess('Node deleted successfully!');
          this.refreshTree(); // Reload the tree to remove the deleted node
          
          // Clear selection if deleted node was selected
          if (this.selectedNode?.node_id === node.node_id) {
            this.selectedNode = null;
          }
          
          // Emit the event
          this.nodeDeleted.emit(node);
        },
        error: (error) => {
          console.error('Error deleting node:', error);
          const errorMessage = error.error?.message || 'Failed to delete node. Please try again.';
          this.showError(errorMessage);
        }
      });
  }

  getNodeIcon(node: AdminTreeNodeLocal): string {
    return node.icon || (node.type === 'operation' ? 'folder' : 'description');
  }

  isNodeSelected(node: AdminTreeNodeLocal): boolean {
    return this.selectedNode?.node_id === node.node_id;
  }

  getNodeTooltip(node: AdminTreeNodeLocal): string {
    return node.description || `${node.type}: ${node.name}`;
  }

  // Helper methods
  private generateNodeId(parentNode: AdminTreeNodeLocal, nodeName: string): string {
    const baseName = nodeName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const siblingCount = parentNode.children?.length || 0;
    return `${parentNode.node_id}-${baseName}-${siblingCount + 1}`;
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  // Collapsed state methods
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.collapseStateChanged.emit(this.isCollapsed);
  }

  getOperationNodes(): AdminTreeNodeLocal[] {
    return this.dataSource.data.filter(node => node.type === 'operation');
  }

  getCollapsedLabel(node: AdminTreeNodeLocal): string {
    // Return first letter or appropriate abbreviation for collapsed view
    if (node.name.length === 0) return '?';
    
    // Handle special cases for better abbreviations
    const name = node.name.toLowerCase();
    if (name.includes('database') || name.includes('db')) return 'DB';
    if (name.includes('user') || name.includes('account')) return 'U';
    if (name.includes('system') || name.includes('sys')) return 'S';
    if (name.includes('config') || name.includes('settings')) return 'C';
    if (name.includes('report') || name.includes('analytics')) return 'R';
    
    // Default to first letter uppercase
    return node.name.charAt(0).toUpperCase();
  }
}
